import { useEffect, useRef } from "react";
import { useTheme } from "../../hooks/useTheme";

// [r, g, b] triplets for particle colors
const PALETTE = [
    [139, 92, 246],   // violet
    [192, 132, 252],  // lavender
    [167, 139, 250],  // soft violet
    [34, 211, 238],   // cyan
    [125, 211, 252],  // sky
];

function lerp(a, b, t) {
    return a + (b - a) * t;
}

function buildParticles(w, h) {
    return Array.from({ length: 65 }, () => {
        const [r, g, b] = PALETTE[Math.floor(Math.random() * PALETTE.length)];
        return {
            x: Math.random() * w,
            y: Math.random() * h,
            size: 0.5 + Math.random() * 2.2,
            baseAlpha: 0.08 + Math.random() * 0.28,
            r, g, b,
            vx: (Math.random() - 0.5) * 0.14,
            vy: (Math.random() - 0.5) * 0.14,
            // mouse offset (current / target)
            ox: 0, oy: 0,
            mRadius: 90 + Math.random() * 140,
            mForce: 0.6 + Math.random() * 1.2,
        };
    });
}

export default function AmbientDust() {
    const { theme } = useTheme();
    const canvasRef = useRef(null);

    useEffect(() => {
        if (theme !== "light") return;

        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");

        const prefersReduced = window.matchMedia(
            "(prefers-reduced-motion: reduce)"
        ).matches;

        let raf;
        let mouseX = window.innerWidth / 2;
        let mouseY = window.innerHeight / 2;
        let scrollY = 0;

        // ── Resize ────────────────────────────────────────────────
        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        resize();
        window.addEventListener("resize", resize);

        // ── Events ────────────────────────────────────────────────
        const onMouse = (e) => {
            mouseX = e.clientX;
            mouseY = e.clientY;
        };
        const onScroll = () => {
            scrollY = window.scrollY;
        };
        window.addEventListener("mousemove", onMouse);
        window.addEventListener("scroll", onScroll, { passive: true });

        // ── Particles ─────────────────────────────────────────────
        const particles = buildParticles(canvas.width, canvas.height);

        // ── Scroll fade curve ─────────────────────────────────────
        // 0 → 1*vh: full (hero = 80%)
        // 1 → 2*vh: fade from 80% → 28% (next section)
        // 2 → 3*vh: fade from 28% → 0   (rest of page)
        function getScrollFade() {
            const vh = window.innerHeight;
            if (scrollY <= vh) return 0.82;
            if (scrollY <= 2 * vh)
                return 0.82 - ((scrollY - vh) / vh) * 0.54; // 0.82 → 0.28
            if (scrollY <= 3 * vh)
                return 0.28 - ((scrollY - 2 * vh) / vh) * 0.28; // 0.28 → 0
            return 0;
        }

        // ── Draw loop ─────────────────────────────────────────────
        const draw = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            const scrollFade = getScrollFade();

            if (scrollFade > 0.005) {
                for (const p of particles) {
                    if (!prefersReduced) {
                        // Gentle autonomous drift
                        p.x += p.vx;
                        p.y += p.vy;

                        // Wrap around viewport
                        if (p.x < -8) p.x = canvas.width + 8;
                        else if (p.x > canvas.width + 8) p.x = -8;
                        if (p.y < -8) p.y = canvas.height + 8;
                        else if (p.y > canvas.height + 8) p.y = -8;

                        // Mouse parallax: gentle pull toward cursor
                        const dx = mouseX - p.x;
                        const dy = mouseY - p.y;
                        const dist = Math.sqrt(dx * dx + dy * dy);
                        if (dist < p.mRadius) {
                            const influence =
                                (1 - dist / p.mRadius) *
                                p.mForce *
                                0.012;
                            p.ox = lerp(p.ox, dx * influence, 0.04);
                            p.oy = lerp(p.oy, dy * influence, 0.04);
                        } else {
                            p.ox = lerp(p.ox, 0, 0.025);
                            p.oy = lerp(p.oy, 0, 0.025);
                        }
                    }

                    const px = p.x + p.ox;
                    const py = p.y + p.oy;
                    const alpha = p.baseAlpha * scrollFade;

                    // Core dot
                    ctx.beginPath();
                    ctx.arc(px, py, p.size, 0, Math.PI * 2);
                    ctx.fillStyle = `rgba(${p.r},${p.g},${p.b},${alpha})`;
                    ctx.fill();

                    // Soft glow halo
                    const glowR = p.size * 5.5;
                    const grd = ctx.createRadialGradient(
                        px, py, 0,
                        px, py, glowR
                    );
                    grd.addColorStop(
                        0,
                        `rgba(${p.r},${p.g},${p.b},${alpha * 0.28})`
                    );
                    grd.addColorStop(
                        1,
                        `rgba(${p.r},${p.g},${p.b},0)`
                    );
                    ctx.beginPath();
                    ctx.arc(px, py, glowR, 0, Math.PI * 2);
                    ctx.fillStyle = grd;
                    ctx.fill();
                }
            }

            raf = requestAnimationFrame(draw);
        };

        draw();

        // ── Cleanup ───────────────────────────────────────────────
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", resize);
            window.removeEventListener("mousemove", onMouse);
            window.removeEventListener("scroll", onScroll);
        };
    }, [theme]);

    if (theme !== "light") return null;

    return (
        <canvas
            ref={canvasRef}
            aria-hidden
            style={{
                position: "fixed",
                inset: 0,
                pointerEvents: "none",
                zIndex: 2,
            }}
        />
    );
}
